<template>
  <transition name="slide-up" appear>
    <aside
      v-if="isVisible && !settingsOpen"
      class="fixed bottom-0 md:bottom-5 right-0 md:right-5 bg-white p-8 max-w-[475px] rounded-tl-md md:rounded-md transition-[bottom_right_border-radius] z-50 text-sm shadow"
    >
      <p>
        <span>{{ $t("gdpr.banner.description") }}</span>
        <LangRouterLink
          :to="{ name: $routes.POLICIES }"
          class="ml-1 underline hover:text-lilac-pale transition-colors"
        >
          {{ $t("gdpr.banner.more") }}
        </LangRouterLink>
      </p>
      <div class="mt-8 flex items-center gap-8">
        <button class="button" @click="acceptAll">{{ $t("gdpr.banner.accept") }}</button>
        <button
          class="flex items-center gap-2.5 hover:gap-3 transition-[gap] cursor-pointer"
          @click="settingsOpen = true"
        >
          <span>{{ $t("gdpr.banner.settings") }}</span>
          <IconSprite id="arrow" class="text-xs" />
        </button>
      </div>
    </aside>
  </transition>

  <transition name="slide-right" appear>
    <div
      v-if="settingsOpen"
      class="fixed top-0 right-0 w-full h-full z-50 text-sm"
      style="backface-visibility: hidden"
      @click="settingsOpen = false"
    >
      <aside
        class="relative w-full h-full md:h-[calc(100dvh-2.5rem)] bg-white max-w-[475px] ml-auto md:mr-5 md:mt-5 shadow md:rounded-md"
        @click.stop
      >
        <div class="overflow-auto h-full pt-8 px-8 pb-44">
          <section>
            <h2 class="text-xl mb-5">{{ $t("gdpr.settings.title") }}</h2>
            <p class="description">{{ $t("gdpr.settings.description") }}</p>
            <p class="description mt-3">
              <span>{{ $t("gdpr.settings.more") }}</span>
              <LangRouterLink
                :to="{ name: $routes.POLICIES }"
                class="router-link ml-1 underline hover:text-lilac-pale transition-colors"
              >
                {{ `${$t("policies.title")}.` }}
              </LangRouterLink>
            </p>
          </section>
          <section class="mt-6">
            <div class="flex justify-between items-center mb-5">
              <h2 class="text-xl">{{ $t("gdpr.settings.analytics.title") }}</h2>
              <div
                :class="[
                  'w-17.5 h-8 rounded-full p-0.5 cursor-pointer transition-[background-color] relative flex items-center px-0.5 select-none',
                  { 'bg-(--main-color) text-white': gtag, 'bg-white': !gtag },
                ]"
                @click.stop="gtag = !gtag"
              >
                <Transition name="fade" mode="out-in">
                  <span
                    :class="[
                      'text-center uppercase text-[14px] transition-all pt-0.5 bg-transparent pl-2 pr-1.5 ml-0',
                      { 'text-lilac-base order-2 !ml-auto': !gtag },
                    ]"
                    :key="`gtag-${gtag ? 'on' : 'off'}`"
                    >{{ gtag ? "on" : "off" }}</span
                  >
                </Transition>
                <span
                  :class="[
                    'w-7 aspect-square bg-white rounded-full absolute left-0.5 ml-auto top-0.5 transition-all translate-x-[38px]',
                    { '!bg-(--main-color) !-translate-x-0': !gtag },
                  ]"
                />
              </div>
            </div>
            <p class="description">{{ $t("gdpr.settings.analytics.description") }}</p>
          </section>
        </div>
        <button class="button absolute bottom-8 left-8" @click="saveSettings">
          {{ $t("gdpr.settings.save") }}
        </button>
      </aside>
    </div>
  </transition>
</template>

<script lang="ts">
import { ref, onBeforeMount, onMounted } from "vue";
import { addGtag, optOut } from "vue-gtag";
import { IconSprite, LangRouterLink } from "@/components/helpers";

export default {
  name: "Gdpr",
  components: { IconSprite, LangRouterLink },
  setup() {
    const gtag = ref(false);
    const isVisible = ref(false);
    const settingsOpen = ref(false);

    const loadGtag = async () => {
      if (import.meta.env.VITE_APP_GTAG_ID) {
        try {
          await addGtag();
          localStorage.setItem("gdpr-gtag", "1");
        } catch (e) {
          console.error("Error loading gtag", e);
        }
      }
    };

    const acceptAll = () => {
      localStorage.setItem("gdpr-consent", "1");
      loadGtag();
      isVisible.value = false;
    };

    const disableGtag = () => {
      optOut();
      localStorage.removeItem("gdpr-gtag");
    };

    const saveSettings = () => {
      if (gtag.value) {
        loadGtag();
      } else {
        disableGtag();
      }
      settingsOpen.value = false;
      isVisible.value = false;

      if (gtag.value) localStorage.setItem("gdpr-consent", "1");
    };

    onBeforeMount(() => {
      if (localStorage.getItem("gdpr-consent")) {
        isVisible.value = false;
      } else {
        setTimeout(() => {
          isVisible.value = true;
        }, 3000);
      }

      if (localStorage.getItem("gdpr-gtag")) {
        gtag.value = true;
      }
    });

    onMounted(() => {
      if (gtag.value) {
        loadGtag();
      }
      gtag.value = true;
    });

    return {
      gtag,
      isVisible,
      settingsOpen,
      loadGtag,
      acceptAll,
      disableGtag,
      saveSettings,
    };
  },
};
</script>
